import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { cn } from '@/lib/utils';
import { gapSizes } from '@/theme/size-tokens';

export type ScrollNavItem = {
  id: string;
  label: string;
};

export type ScrollNavProps = {
  items: ScrollNavItem[];
  sticky?: boolean;
  className?: string;
  scrollContainerClassName?: string;
  showBorder?: boolean;
  blur?: boolean;
  spacing?: 'xs' | 'sm' | 'md' | 'lg';
  renderItem?: (
    item: ScrollNavItem,
    isActive: boolean,
    onClick: () => void
  ) => ReactNode;
  onActiveChange?: (activeId: string) => void;
  observerRootMargin?: string;
  observerThreshold?: number;
};

function ScrollNav({
  items,
  sticky = true,
  className,
  scrollContainerClassName,
  showBorder = true,
  blur = true,
  spacing = 'sm',
  renderItem,
  onActiveChange,
  observerRootMargin = '-100px 0px -50% 0px',
  observerThreshold = 0,
}: Readonly<ScrollNavProps>) {
  const [activeId, setActiveId] = useState<string>(items[0]?.id || '');
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleClick = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const scrollLeft = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollBy({
      left: -container.clientWidth * 0.75,
      behavior: 'smooth',
    });
  }, []);

  const scrollRight = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollBy({
      left: container.clientWidth * 0.75,
      behavior: 'smooth',
    });
  }, []);

  const updateScrollShadows = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    const isScrollable = scrollWidth > clientWidth;
    setShowLeftShadow(isScrollable && scrollLeft > 0);
    setShowRightShadow(
      isScrollable && scrollLeft < scrollWidth - clientWidth - 20
    );
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const resizeObserver = new ResizeObserver(updateScrollShadows);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [updateScrollShadows]);

  useEffect(() => {
    const timer = setTimeout(updateScrollShadows, 0);
    return () => clearTimeout(timer);
  }, [updateScrollShadows]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener('scroll', updateScrollShadows);
    return () => container.removeEventListener('scroll', updateScrollShadows);
  }, [updateScrollShadows]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);
        if (visibleEntries.length > 0) {
          const sortedVisible = visibleEntries.sort((a, b) => {
            const aIndex = items.findIndex((item) => item.id === a.target.id);
            const bIndex = items.findIndex((item) => item.id === b.target.id);
            return aIndex - bIndex;
          });
          const newActiveId = sortedVisible[0].target.id;
          setActiveId(newActiveId);
          onActiveChange?.(newActiveId);
        }
      },
      { rootMargin: observerRootMargin, threshold: observerThreshold }
    );
    items.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, [items, observerRootMargin, observerThreshold, onActiveChange]);

  if (items.length === 0) return null;

  const bgClass = blur ? 'bg-background/95 backdrop-blur-sm' : 'bg-background';

  const scrollClasses = cn(
    'flex flex-row flex-nowrap items-stretch',
    gapSizes[spacing],
    'overflow-x-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent',
    scrollContainerClassName
  );

  return (
    <div
      className={cn(
        'relative',
        sticky && 'sticky top-0 z-99',
        bgClass,
        showBorder && 'border-b border-border',
        className
      )}
    >
      {showLeftShadow && (
        <button
          onClick={scrollLeft}
          className={cn(
            'absolute top-0 bottom-0 left-0 flex w-8 cursor-pointer items-center justify-center',
            bgClass,
            'hover:bg-muted border-border z-10 border-r transition-colors'
          )}
          aria-label="Scroll left"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-foreground/70"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      )}
      {showLeftShadow && (
        <div
          className={cn(
            'absolute top-0 bottom-0 left-8 w-8 pointer-events-none z-10 to-transparent',
            blur
              ? 'from-background/95 bg-linear-to-r'
              : 'from-background bg-linear-to-r'
          )}
        />
      )}
      {showRightShadow && (
        <div
          className={cn(
            'absolute top-0 right-8 bottom-0 w-8 pointer-events-none z-10 to-transparent',
            blur
              ? 'from-background/95 bg-linear-to-l'
              : 'from-background bg-linear-to-l'
          )}
        />
      )}
      {showRightShadow && (
        <button
          onClick={scrollRight}
          className={cn(
            'absolute top-0 right-0 bottom-0 flex w-8 cursor-pointer items-center justify-center',
            bgClass,
            'hover:bg-muted border-border z-10 border-l transition-colors'
          )}
          aria-label="Scroll right"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-foreground/70"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      )}
      <div ref={scrollContainerRef} className={scrollClasses}>
        {items.map((item) => {
          const isActive = activeId === item.id;
          const onClick = () => handleClick(item.id);
          if (renderItem) {
            return (
              <div key={item.id}>{renderItem(item, isActive, onClick)}</div>
            );
          }
          return (
            <button
              key={item.id}
              onClick={onClick}
              className={cn(
                'rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors',
                isActive
                  ? 'bg-primary text-primary-fg'
                  : 'text-foreground/70 hover:text-foreground hover:bg-muted'
              )}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export { ScrollNav };
export default ScrollNav;
